import {
  addEdge,
  Background,
  BaseEdge,
  ControlButton,
  Controls,
  EdgeLabelRenderer,
  getSmoothStepPath,
  MarkerType,
  MiniMap,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
} from '@ea-lab/reactive-json';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { fetchRoles, fetchWorkflow, saveWorkflow } from './workflowApi.js';
import { StateNode } from './StateNode.jsx';
import { assignParallelOffsets, recalculateEdgeHandles, toDrupal, toFlow } from './workflowConverter.js';

// ---------------------------------------------------------------------------
// Weight-based staircase layout
//
// Rules:
//   - States with the same weight → same Y, spread horizontally
//   - Each weight level → one step lower (Y += ROW_HEIGHT) and one step right
//     (X starts at the next cumulative column), forming a staircase
//
// Columns are cumulative across weight levels so that wide groups (multiple
// same-weight states) don't overlap the next step.
// ---------------------------------------------------------------------------

const ROW_HEIGHT = 85;
const COL_WIDTH  = 230;
const PADDING    = 40;

// Must match PARALLEL_SPACING in workflowConverter.js.
const PARALLEL_SPACING_LAYOUT = 26;

function getLayoutedElements(nodes, edges) {
  // Group nodes by weight, preserving insertion order within each group.
  const byWeight = new Map();
  nodes.forEach((node) => {
    const w = node.data?.weight ?? 0;
    if (!byWeight.has(w)) byWeight.set(w, []);
    byWeight.get(w).push(node);
  });

  // Sort weight levels ascending.
  const sortedWeights = [...byWeight.keys()].sort((a, b) => a - b);

  // Count parallel edges per node+handle.
  const handleCount = new Map();
  edges.forEach((edge) => {
    if (!edge.targetHandle) return;
    const key = `${edge.target}__${edge.targetHandle}`;
    handleCount.set(key, (handleCount.get(key) ?? 0) + 1);
  });

  // Same formula for both left and right fans.
  // Kicks in from the 3rd parallel edge; grows at PARALLEL_SPACING/2 per edge.
  const fanExtra = (n) => Math.max(0, n - 2) * (PARALLEL_SPACING_LAYOUT / 2);

  // Assign positions.
  // - left-incoming fan  → shifts the target node itself downward
  // - right-incoming fan → shifts all subsequent weight levels downward
  const positions = {};
  let cumulativeCol = 0;
  let accRightExtraY = 0;

  sortedWeights.forEach((weight, weightIdx) => {
    const group = byWeight.get(weight);

    group.forEach((node, groupIdx) => {
      const leftExtra = fanExtra(handleCount.get(`${node.id}__target-left`) ?? 0);
      positions[node.id] = {
        x: PADDING + (cumulativeCol + groupIdx) * COL_WIDTH,
        y: PADDING + weightIdx * ROW_HEIGHT + accRightExtraY + leftExtra,
      };
    });

    // Left extra already shifted this level's nodes; it must also cascade to
    // subsequent levels so they inherit the full accumulated offset.
    // Right extra is added on top: X's return-transition fan pushes Y further down.
    const maxLeftExtra = Math.max(
      0,
      ...group.map((node) => fanExtra(handleCount.get(`${node.id}__target-left`) ?? 0)),
    );
    const maxRightExtra = Math.max(
      0,
      ...group.map((node) => fanExtra(handleCount.get(`${node.id}__target-right`) ?? 0)),
    );
    accRightExtraY += maxLeftExtra + maxRightExtra;

    cumulativeCol += group.length;
  });

  const layoutedNodes = nodes.map((node) => ({
    ...node,
    position: positions[node.id] ?? node.position,
  }));

  return { nodes: layoutedNodes, edges };
}

// ---------------------------------------------------------------------------
// Self-loop edge — renders a circular arc when source === target
// ---------------------------------------------------------------------------

function SelfLoopEdge({ id, sourceX, sourceY, targetX, targetY, label, markerEnd, style, selected, data }) {
  const r = 50;
  // Arc that exits from the bottom handle and enters at the left handle,
  // curving down-left to form a visible loop below and to the left of the node.
  const path = `M ${sourceX} ${sourceY} C ${sourceX} ${sourceY + r}, ${targetX - r} ${targetY}, ${targetX} ${targetY}`;
  const labelX = (sourceX + targetX) / 2 - r * 0.4;
  const labelY = (sourceY + targetY) / 2 + r * 0.6;

  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{ transform: `translate(-50%, -100%) translate(${labelX}px,${labelY}px)` }}
            className={`absolute text-[11px] px-1.5 py-0.5 rounded bg-white border pointer-events-auto cursor-pointer
              ${selected ? 'border-iris-500 text-iris-600' : 'border-slate-200 text-slate-600'}`}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Parallel edge — smoothstep with an angled stem to fan out overlapping arrows
//
// When multiple transitions share the same target handle, each edge gets a
// `parallelOffset` (px) from assignParallelOffsets(). The edge routes to an
// "approach point" shifted by that offset, then draws a short diagonal stem
// to the actual target — creating a fan/fishbone pattern.
// ---------------------------------------------------------------------------

const STEM_LENGTH = 50;

function ParallelEdge({
  id,
  sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition,
  markerEnd, style, label, selected, data,
}) {
  const offset = data?.parallelOffset ?? 0;

  // Self-loop: rectangular path — go down below the approach point, left
  // (label here), up to approach level, then diagonal stem to target-left.
  if (data?.isSelfLoop) {
    const approachX = targetX - STEM_LENGTH;
    const approachY = targetY + offset;
    // loopY must be below both sourceY (bottom of node) and approachY, with margin.
    const loopY    = Math.max(sourceY, approachY) + PARALLEL_SPACING_LAYOUT;
    const selfPath = `M ${sourceX} ${sourceY} L ${sourceX} ${loopY} L ${approachX} ${loopY} L ${approachX} ${approachY} L ${targetX} ${targetY}`;
    const labelX   = (sourceX + approachX) / 2;
    const labelY   = loopY;
    return (
      <>
        <BaseEdge id={id} path={selfPath} markerEnd={markerEnd} style={style} />
        {label && (
          <EdgeLabelRenderer>
            <div
              style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)` }}
              className={`absolute text-[11px] px-1.5 py-0.5 rounded bg-white border pointer-events-auto cursor-pointer nodrag nopan
                ${selected ? 'border-iris-500 text-iris-600' : 'border-slate-200 text-slate-600'}`}
            >
              {label}
            </div>
          </EdgeLabelRenderer>
        )}
      </>
    );
  }

  if (Math.abs(offset) < 1) {
    // Single edge or center of a group — plain smoothstep directly to target.
    const [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX, sourceY, sourcePosition,
      targetX, targetY, targetPosition,
      borderRadius: 10,
    });
    return (
      <>
        <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
        {label && (
          <EdgeLabelRenderer>
            <div
              style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)` }}
              className={`absolute text-[11px] px-1.5 py-0.5 rounded bg-white border pointer-events-auto cursor-pointer nodrag nopan
                ${selected ? 'border-iris-500 text-iris-600' : 'border-slate-200 text-slate-600'}`}
            >
              {label}
            </div>
          </EdgeLabelRenderer>
        )}
      </>
    );
  }

  // Compute approach point: STEM_LENGTH px in front of the target handle
  // (in the direction of travel), offset perpendicularly.
  // "In front" means opposite to the handle's incoming direction:
  //   target-left  → edge arrives from the left  → approach is to the LEFT  (targetX - stem)
  //   target-right → edge arrives from the right → approach is to the RIGHT (targetX + stem)
  //   target-top   → edge arrives from above     → approach is ABOVE        (targetY - stem)
  //   target-bottom→ edge arrives from below     → approach is BELOW        (targetY + stem)
  let approachX = targetX;
  let approachY = targetY;
  if (targetPosition === Position.Left) {
    approachX = targetX - STEM_LENGTH;
    approachY = targetY + offset;
  } else if (targetPosition === Position.Right) {
    approachX = targetX + STEM_LENGTH;
    approachY = targetY + offset;
  } else if (targetPosition === Position.Top) {
    approachX = targetX + offset;
    approachY = targetY - STEM_LENGTH;
  } else {
    approachX = targetX + offset;
    approachY = targetY + STEM_LENGTH;
  }

  // Route from source to approach point, then straight angled stem to actual target.
  const [mainPath, labelX, labelY] = getSmoothStepPath({
    sourceX, sourceY, sourcePosition,
    targetX: approachX, targetY: approachY,
    targetPosition,
    borderRadius: 10,
  });
  const fullPath = `${mainPath} L ${targetX} ${targetY}`;

  return (
    <>
      <BaseEdge id={id} path={fullPath} markerEnd={markerEnd} style={style} />
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)` }}
            className={`absolute text-[11px] px-1.5 py-0.5 rounded bg-white border pointer-events-auto cursor-pointer nodrag nopan
              ${selected ? 'border-iris-500 text-iris-600' : 'border-slate-200 text-slate-600'}`}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Node & edge type registries
// ---------------------------------------------------------------------------
// Weight reindexing
//
// Assigns sequential weights 0, 1, 2, … to all nodes, sorted by their current
// weight. Array order is used as a tiebreaker so equal-weight nodes get a
// deterministic, stable ordering. After this no two nodes share the same weight.
// ---------------------------------------------------------------------------

function reindexWeights(nodes) {
  const sorted = [...nodes].sort((a, b) => {
    const wa = a.data?.weight ?? 0;
    const wb = b.data?.weight ?? 0;
    if (wa !== wb) return wa - wb;
    return nodes.indexOf(a) - nodes.indexOf(b);
  });
  return sorted.map((node, idx) => ({
    ...node,
    data: { ...node.data, weight: idx },
  }));
}

// ---------------------------------------------------------------------------

const NODE_TYPES = { state: StateNode };
const EDGE_TYPES = { selfLoop: SelfLoopEdge, parallel: ParallelEdge };

// ---------------------------------------------------------------------------
// Unique ID generator for new states/transitions
// ---------------------------------------------------------------------------

let _idCounter = 1;
const genId = (prefix) => `${prefix}_${Date.now()}_${_idCounter++}`;

// Builds a Drupal-safe transition machine name from source and target state IDs.
// Max 64 chars (safe Drupal convention); truncates each part proportionally if needed.
function makeTransitionId(sourceId, targetId) {
  const sep = '__to__';
  const MAX = 64;
  const full = `${sourceId}${sep}${targetId}`;
  if (full.length <= MAX) return full;
  const available = MAX - sep.length;
  const srcMax = Math.ceil(available / 2);
  const tgtMax = available - srcMax;
  return `${sourceId.slice(0, srcMax)}${sep}${targetId.slice(0, tgtMax)}`;
}

// Converts a human label to a Drupal-style machine name:
// lowercase, accents stripped, non-alphanumeric → underscore, deduplicated, trimmed.
function toMachineId(label) {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

// ---------------------------------------------------------------------------
// State panel (right sidebar when a node is selected)
//
// Changes apply immediately: text/number fields on blur, checkbox on change.
// No separate "Appliquer" step required.
// ---------------------------------------------------------------------------

function StatePanel({ node, minWeight, maxWeight, onChange, onDelete, readOnly }) {
  const [label, setLabel] = useState(node.data.label ?? '');
  const [weight, setWeight] = useState(node.data.weight ?? 0);

  // Reset local inputs when the selected node changes or its data is updated
  // externally (e.g. after a save round-trip).
  useEffect(() => {
    setLabel(node.data.label ?? '');
    setWeight(node.data.weight ?? 0);
  }, [node.id, node.data.label, node.data.weight]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between border-b pb-3 min-w-0 gap-2">
        <div className="min-w-0 flex-1 overflow-hidden">
          <h2 className="text-base font-semibold text-slate-800">État</h2>
          <span className="inline-block max-w-full text-xs font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded mt-0.5 truncate align-middle" title={node.id}>
            {node.id}
          </span>
        </div>
        {!readOnly && (
          <button
            onClick={() => onDelete(node.id, 'node')}
            title="Supprimer l'état"
            className="shrink-0 text-slate-300 hover:text-red-500 transition-colors mt-1"
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Libellé</label>
        <input
          type="text"
          value={label}
          disabled={readOnly}
          onChange={(e) => {
            setLabel(e.target.value);
            onChange(node.id, { label: e.target.value });
          }}
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-iris-500 focus:border-iris-500 outline-none disabled:bg-slate-100 disabled:text-slate-500"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Poids (ordre)</label>
        <input
          type="number"
          value={weight}
          min={minWeight}
          max={maxWeight}
          disabled={readOnly}
          onChange={(e) => {
            setWeight(e.target.value);
            const val = parseInt(e.target.value, 10);
            if (!isNaN(val)) onChange(node.id, { weight: val });
          }}
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-iris-500 focus:border-iris-500 outline-none disabled:bg-slate-100 disabled:text-slate-500"
        />
      </div>

      <div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={node.data.published ?? false}
            disabled={readOnly}
            onChange={(e) => onChange(node.id, { published: e.target.checked })}
            className="w-4 h-4 text-iris-600 rounded border-slate-300 focus:ring-iris-500 disabled:opacity-50"
          />
          <span className="text-sm text-slate-700 font-medium">État publié</span>
        </label>
        <p className="text-xs text-slate-400 mt-1 ml-7">
          Un état publié rend le contenu visible publiquement.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transition panel (right sidebar when an edge is selected)
//
// Changes apply immediately: label field on blur, role checkboxes on change.
// No separate "Appliquer" step required.
// ---------------------------------------------------------------------------

function TransitionPanel({ edge, roles, onChange, onDelete, readOnly }) {
  const [label, setLabel] = useState(edge.data?.label ?? '');
  const [allowedRoles, setAllowedRoles] = useState(edge.data?.allowedRoles ?? []);

  // Reset local state when the selected edge changes or its data is updated.
  useEffect(() => {
    setLabel(edge.data?.label ?? '');
    setAllowedRoles(edge.data?.allowedRoles ?? []);
  }, [edge.id, edge.data?.label, edge.data?.allowedRoles]);

  const toggleRole = (roleId) => {
    const newRoles = allowedRoles.includes(roleId)
      ? allowedRoles.filter((r) => r !== roleId)
      : [...allowedRoles, roleId];
    setAllowedRoles(newRoles);
    onChange(edge.id, { label, allowedRoles: newRoles });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between border-b pb-3 min-w-0 gap-2">
        <div className="min-w-0 flex-1 overflow-hidden">
          <h2 className="text-base font-semibold text-slate-800">Transition</h2>
          <span
            className="inline-block max-w-full text-xs font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded mt-0.5 truncate align-middle"
            title={edge.data?.transitionId ?? edge.id}
          >
            {edge.data?.transitionId ?? edge.id}
          </span>
        </div>
        {!readOnly && (
          <button
            onClick={() => onDelete(edge.id, 'edge')}
            title="Supprimer la transition"
            className="shrink-0 text-slate-300 hover:text-red-500 transition-colors mt-1"
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>

      <div className="text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
        <span className="font-semibold">{edge.source}</span>
        <span className="mx-1.5">→</span>
        <span className="font-semibold">{edge.target}</span>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Libellé</label>
        <input
          type="text"
          value={label}
          disabled={readOnly}
          onChange={(e) => {
            setLabel(e.target.value);
            onChange(edge.id, { label: e.target.value });
          }}
          className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-iris-500 focus:border-iris-500 outline-none disabled:bg-slate-100 disabled:text-slate-500"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-2">
          Rôles autorisés
        </label>
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 space-y-2 max-h-64 overflow-y-auto">
          {roles.length === 0 && (
            <p className="text-xs text-slate-400 italic">Chargement des rôles…</p>
          )}
          {roles.map((role) => (
            <label
              key={role.id}
              className={`flex items-center gap-3 py-0.5 ${readOnly ? 'cursor-default' : 'cursor-pointer hover:text-iris-600'}`}
            >
              <input
                type="checkbox"
                disabled={readOnly}
                checked={allowedRoles.includes(role.id)}
                onChange={() => toggleRole(role.id)}
                className="w-4 h-4 text-iris-600 rounded border-slate-300 focus:ring-iris-500 disabled:opacity-50"
              />
              <span className="text-sm text-slate-700">{role.label}</span>
            </label>
          ))}
        </div>
        {!readOnly && (
          <p className="text-xs text-slate-400 mt-1.5">
            Aucun rôle sélectionné = transition accessible à tous les utilisateurs authentifiés.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New state modal — label + machine name picker
// ---------------------------------------------------------------------------

function NewStateModal({ existingIds, onConfirm, onCancel }) {
  const [label, setLabel] = useState('');
  const [machineId, setMachineId] = useState('');
  const [machineEdited, setMachineEdited] = useState(false);

  const handleLabelChange = (e) => {
    const val = e.target.value;
    setLabel(val);
    if (!machineEdited) setMachineId(toMachineId(val));
  };

  const handleMachineChange = (e) => {
    const raw = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_');
    setMachineId(raw);
    setMachineEdited(true);
  };

  const isDuplicate = existingIds.includes(machineId);
  const isValid = label.trim().length > 0 && machineId.length > 0 && !isDuplicate;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onConfirm({ label: label.trim(), machineId });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6 flex flex-col gap-5"
      >
        <h3 className="text-base font-semibold text-slate-800">Nouvel état</h3>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Libellé</label>
          <input
            autoFocus
            type="text"
            value={label}
            onChange={handleLabelChange}
            placeholder="Ex : En cours de révision"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-iris-500 focus:border-iris-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">
            Nom machine
            <span className="ml-1 font-normal text-slate-400">(lettres, chiffres, _)</span>
          </label>
          <input
            type="text"
            value={machineId}
            onChange={handleMachineChange}
            placeholder="ex : en_cours_de_revision"
            className={`w-full border rounded-md px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-iris-500 outline-none ${
              isDuplicate ? 'border-red-400 focus:border-red-400 focus:ring-red-300' : 'border-slate-300 focus:border-iris-500'
            }`}
          />
          {isDuplicate && (
            <p className="text-xs text-red-500 mt-1">Ce nom machine existe déjà.</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!isValid}
            className="px-4 py-2 text-sm font-semibold text-white bg-iris-600 rounded-md hover:bg-iris-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Créer l&apos;état
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfirmModal — generic destructive-action confirmation
// ---------------------------------------------------------------------------

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-xs mx-4 p-6 flex flex-col gap-5">
        <p className="text-sm text-slate-700">{message}</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TransitionPickerModal — pick a state to add as Vers/Depuis
// ---------------------------------------------------------------------------

function TransitionPickerModal({ direction, pivotNodeId, nodes, existingEdges, onConfirm, onClose }) {
  const [selected, setSelected] = useState(new Set());
  const [label, setLabel] = useState('Nouvelle transition');

  // Exclude only pairs that already exist (self-transitions are allowed).
  const candidates = nodes.filter((n) => {
    if (direction === 'out') {
      return !existingEdges.some((e) => e.source === pivotNodeId && e.target === n.id);
    }
    return !existingEdges.some((e) => e.source === n.id && e.target === pivotNodeId);
  });

  const title = direction === 'out' ? "Vers — choisir les états de destination" : "Depuis — choisir les états d'origine";

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selected.size === 0) return;
    onConfirm([...selected], label.trim() || 'Nouvelle transition');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-sm mx-4 p-6 flex flex-col gap-5"
      >
        <h3 className="text-base font-semibold text-slate-800">{title}</h3>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Label</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Nouvelle transition"
            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-iris-400"
          />
        </div>

        <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
          {candidates.map((n) => (
            <label
              key={n.id}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer border transition-colors ${
                selected.has(n.id)
                  ? 'border-iris-500 bg-iris-50'
                  : 'border-transparent hover:bg-slate-50'
              }`}
            >
              <input
                type="checkbox"
                value={n.id}
                checked={selected.has(n.id)}
                onChange={() => toggle(n.id)}
                className="accent-iris-600"
              />
              <span className="text-sm font-medium text-slate-700">
                {n.data?.label || n.id}
              </span>
              <span className="ml-auto text-xs font-mono text-slate-400">{n.id}</span>
            </label>
          ))}
          {candidates.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">Toutes les transitions existent déjà.</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={selected.size === 0}
            className="px-4 py-2 text-sm font-semibold text-white bg-iris-600 rounded-md hover:bg-iris-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Ajouter {selected.size > 1 ? `(${selected.size})` : ''}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inner editor (needs ReactFlowProvider context)
// ---------------------------------------------------------------------------

function WorkflowEditorInner({ workflowId, readOnly }) {
  const { fitView, updateNodeData, setEdges: rfSetEdges, getNodes, getEdges } = useReactFlow();

  // Remote data
  const [workflowMeta, setWorkflowMeta] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Selection
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);

  // Guide panel toggle
  const [showGuide, setShowGuide] = useState(false);

  // New state modal
  const [newStateModalOpen, setNewStateModalOpen] = useState(false);

  // Transition picker modal: { direction: 'out'|'in', nodeId: string } | null
  const [transitionModal, setTransitionModal] = useState(null);

  // Generic confirmation modal: { message: string, onConfirm: fn } | null
  const [confirmModal, setConfirmModal] = useState(null);
  const askConfirm = useCallback((message, onConfirm) => {
    setConfirmModal({ message, onConfirm });
  }, []);

  // Ref to keep current edges in sync for the onConnect callback.
  const edgesRef = useRef(edges);
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  // Apply Dagre auto-layout + parallel offset assignment to current nodes/edges.
  const applyLayout = useCallback(
    (nodesToLayout, edgesToLayout) => {
      const reindexed  = reindexWeights(nodesToLayout);
      const recalcEdges = recalculateEdgeHandles(edgesToLayout, reindexed);
      const { nodes: ln, edges: le } = getLayoutedElements(reindexed, recalcEdges);
      setNodes(ln);
      setEdges(assignParallelOffsets(le));
      requestAnimationFrame(() => fitView({ padding: 0.15 }));
    },
    [setNodes, setEdges, fitView],
  );

  // Load workflow + roles on mount.
  useEffect(() => {
    if (!workflowId) return;
    setLoading(true);
    setError(null);

    Promise.all([fetchWorkflow(workflowId), fetchRoles()])
      .then(([config, roleList]) => {
        setWorkflowMeta({ id: config.id, label: config.label, type: config.type });
        setRoles(roleList);
        const { nodes: n, edges: e } = toFlow(config);
        applyLayout(n, e);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [workflowId, applyLayout]);

  // Keep selected node/edge data in sync with current graph state.
  // Use the React state arrays (nodes / edges) rather than getNodes()/getEdges()
  // so that setNodes-based updates (e.g. weight reordering) are reflected
  // immediately without waiting for the Zustand store to catch up.
  useEffect(() => {
    if (!selectedNode) return;
    const updated = nodes.find((n) => n.id === selectedNode.id);
    if (updated) setSelectedNode(updated);
  }, [nodes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedEdge) return;
    const updated = edges.find((e) => e.id === selectedEdge.id);
    if (updated) setSelectedEdge(updated);
  }, [edges]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Graph callbacks
  // -------------------------------------------------------------------------

  // Add a directed transition from sourceNodeId to targetNodeId.
  // Guards against duplicate pairs (A→B unique, but A→B and B→A can coexist).
  const addTransition = useCallback(
    (sourceNodeId, targetNodeId) => {
      if (!sourceNodeId || !targetNodeId) return;
      const alreadyExists = getEdges().some(
        (e) => e.source === sourceNodeId && e.target === targetNodeId,
      );
      if (alreadyExists) return;

      const transitionId = makeTransitionId(sourceNodeId, targetNodeId);
      const allNodes = getNodes();
      const sourceNode = allNodes.find((n) => n.id === sourceNodeId);
      const targetNode = allNodes.find((n) => n.id === targetNodeId);
      const sourceWeight = sourceNode?.data?.weight ?? 0;
      const targetWeight = targetNode?.data?.weight ?? 0;
      const isSelfLoop = sourceNodeId === targetNodeId;

      // Same handle assignment as toFlow() in workflowConverter.js.
      let sourceHandle, targetHandle;
      if (isSelfLoop || sourceWeight < targetWeight) {
        sourceHandle = 'source-bottom';
        targetHandle = 'target-left';
      } else if (sourceWeight > targetWeight) {
        sourceHandle = 'source-top';
        targetHandle = 'target-right';
      } else {
        sourceHandle = 'source-right';
        targetHandle = 'target-left';
      }

      const newEdge = {
        source: sourceNodeId,
        target: targetNodeId,
        sourceHandle,
        targetHandle,
        id: `${transitionId}__from__${sourceNodeId}`,
        type: 'parallel',
        markerEnd: { type: MarkerType.ArrowClosed },
        label: 'Nouvelle transition',
        data: {
          transitionId,
          label: 'Nouvelle transition',
          weight: 0,
          allowedRoles: [],
          sourceWeight,
          isSelfLoop,
        },
      };
      setEdges((eds) => assignParallelOffsets(addEdge(newEdge, eds)));
      setTransitionModal(null);
      requestAnimationFrame(() => applyLayout(getNodes(), getEdges()));
    },
    [getEdges, getNodes, setEdges, applyLayout],
  );

  // Batch version: create multiple transitions at once then layout once.
  const addTransitionsBatch = useCallback(
    (pairs, label = 'Nouvelle transition') => { // pairs: [{ source, target }]
      const currentEdges = getEdges();
      const currentNodes = getNodes();
      const newEdges = pairs
        .filter(({ source, target }) =>
          !currentEdges.some((e) => e.source === source && e.target === target),
        )
        .map(({ source, target }) => {
          const transitionId = makeTransitionId(source, target);
          const srcNode = currentNodes.find((n) => n.id === source);
          const tgtNode = currentNodes.find((n) => n.id === target);
          const sourceWeight = srcNode?.data?.weight ?? 0;
          const targetWeight = tgtNode?.data?.weight ?? 0;
          const isSelfLoop = source === target;
          let sourceHandle, targetHandle;
          if (isSelfLoop || sourceWeight < targetWeight) {
            sourceHandle = 'source-bottom'; targetHandle = 'target-left';
          } else if (sourceWeight > targetWeight) {
            sourceHandle = 'source-top'; targetHandle = 'target-right';
          } else {
            sourceHandle = 'source-right'; targetHandle = 'target-left';
          }
          return {
            id: `${transitionId}__from__${source}`,
            source, target, sourceHandle, targetHandle,
            type: 'parallel',
            markerEnd: { type: MarkerType.ArrowClosed },
            label: 'Nouvelle transition',
            data: { transitionId, label, weight: 0, allowedRoles: [], sourceWeight, isSelfLoop },
          };
        });
      if (newEdges.length === 0) return;
      setEdges((eds) => assignParallelOffsets([...eds, ...newEdges.map((e) => ({ ...e, label }))]));
      setTransitionModal(null);
      requestAnimationFrame(() => applyLayout(getNodes(), getEdges()));
    },
    [getEdges, getNodes, setEdges, applyLayout],
  );

  const removeTransition = useCallback(
    (edgeId) => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      setSelectedEdge((prev) => (prev?.id === edgeId ? null : prev));
    },
    [setEdges],
  );

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  const onEdgeClick = useCallback((_, edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  // -------------------------------------------------------------------------
  // Panel operations
  // -------------------------------------------------------------------------

  const updateNode = useCallback(
    (nodeId, patch) => {
      if ('weight' in patch) {
        const rawWeight = patch.weight;
        const currentNodes = getNodes();
        const oldWeight = currentNodes.find((nd) => nd.id === nodeId)?.data?.weight ?? 0;
        if (rawWeight === oldWeight) return;

        // Ordinal shift: displace every node strictly between old and new weight
        // by ±1 to keep weights unique and contiguous before reindexing.
        const shifted = currentNodes.map((nd) => {
          if (nd.id === nodeId) return { ...nd, data: { ...nd.data, weight: rawWeight } };
          const w = nd.data?.weight ?? 0;
          if (rawWeight > oldWeight && w > oldWeight && w <= rawWeight)
            return { ...nd, data: { ...nd.data, weight: w - 1 } };
          if (rawWeight < oldWeight && w >= rawWeight && w < oldWeight)
            return { ...nd, data: { ...nd.data, weight: w + 1 } };
          return nd;
        });

        // Full reorganisation: reindex + recalculate handles + layout + fitView.
        applyLayout(shifted, getEdges());
      } else {
        // For non-layout fields (label, published), updateNodeData writes
        // directly to the Zustand store for an immediate visual update.
        updateNodeData(nodeId, (data) => ({ ...data, ...patch }));
      }
    },
    [updateNodeData, getNodes, getEdges, applyLayout],
  );

  const updateEdge = useCallback(
    (edgeId, patch) => {
      // rfSetEdges (from useReactFlow) updates the internal store directly,
      // ensuring the edge label and data re-render immediately.
      rfSetEdges((eds) =>
        eds.map((e) =>
          e.id === edgeId
            ? {
                ...e,
                label: patch.label ?? e.label,
                data: { ...e.data, ...patch },
              }
            : e,
        ),
      );
    },
    [rfSetEdges],
  );

  const deleteElement = useCallback(
    (elementId, type) => {
      if (type === 'node') {
        setNodes((nds) => nds.filter((n) => n.id !== elementId));
        setEdges((eds) =>
          assignParallelOffsets(
            eds.filter((e) => e.source !== elementId && e.target !== elementId),
          ),
        );
        setSelectedNode(null);
      } else {
        setEdges((eds) => assignParallelOffsets(eds.filter((e) => e.id !== elementId)));
        setSelectedEdge(null);
      }
    },
    [setNodes, setEdges],
  );

  const confirmedDeleteElement = useCallback(
    (elementId, type) => {
      const message = type === 'node'
        ? 'Supprimer cet état ? Toutes ses transitions seront également supprimées.'
        : 'Supprimer cette transition ?';
      askConfirm(message, () => {
        deleteElement(elementId, type);
        setConfirmModal(null);
      });
    },
    [askConfirm, deleteElement],
  );

  const confirmedRemoveTransition = useCallback(
    (edgeId) => {
      askConfirm('Supprimer cette transition ?', () => {
        removeTransition(edgeId);
        setConfirmModal(null);
      });
    },
    [askConfirm, removeTransition],
  );

  const confirmAddState = useCallback(({ label, machineId }) => {
    setNewStateModalOpen(false);
    const currentNodes = getNodes();
    const currentEdges = getEdges();
    const maxWeight = currentNodes.reduce((max, n) => Math.max(max, n.data?.weight ?? 0), -1);
    const newNode = {
      id: machineId,
      type: 'state',
      position: { x: 0, y: 0 },
      data: { stateId: machineId, label, published: false, weight: maxWeight + 1 },
    };
    applyLayout([...currentNodes, newNode], currentEdges);
  }, [getNodes, getEdges, applyLayout]);

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    if (!workflowMeta) return;
    setSaveStatus('saving');
    try {
      // getNodes()/getEdges() read from the internal React Flow store, ensuring
      // we always capture the latest state regardless of React render timing.
      const config = toDrupal(getNodes(), getEdges(), workflowMeta);
      await saveWorkflow(workflowMeta.id, config);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2500);
    } catch (err) {
      console.error('Workflow save error:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    }
  }, [workflowMeta, getNodes, getEdges]);

  // -------------------------------------------------------------------------
  // Render states
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center text-slate-500 text-sm">
        Chargement du workflow…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center flex-col gap-2">
        <p className="text-red-600 font-semibold">Erreur de chargement</p>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full">
      {/* React Flow canvas */}
      <div className="flex-1 h-full bg-slate-50">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={readOnly ? undefined : onNodesChange}
            onEdgesChange={readOnly ? undefined : onEdgesChange}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            nodesDraggable={!readOnly}
            nodesConnectable={false}
            elementsSelectable
            fitView
            deleteKeyCode={readOnly ? null : 'Delete'}
          >
          <Background gap={16} size={1} color="#e2e8f0" />
          <Controls>
            {!readOnly && (
              <ControlButton
                onClick={() => setShowGuide((v) => !v)}
                title="Aide"
                className={showGuide ? 'text-iris-600' : ''}
              >
                ?
              </ControlButton>
            )}
          </Controls>
          <MiniMap
            nodeColor={(n) => (n.data?.published ? '#10b981' : '#94a3b8')}
            maskColor="rgba(248,250,252,0.8)"
          />

          {/* Top toolbar */}
          <Panel position="top-left">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg shadow-sm px-3 py-2">
              <a
                href="/configuration?tab=workflow"
                className="text-slate-400 hover:text-iris-700 transition-colors mr-1 text-sm leading-none"
                title="Retour aux flux"
              >
                ←
              </a>
              <span className="text-slate-300 select-none">|</span>
              <span className="text-sm font-semibold text-slate-700 mr-2">
                {workflowMeta?.label}
              </span>
              {!readOnly && (
                <button
                  onClick={() => setNewStateModalOpen(true)}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-iris-600 text-white rounded-md hover:bg-iris-700 transition-colors"
                >
                  <span className="text-base leading-none">+</span> État
                </button>
              )}
              {!readOnly && (
                <button
                  onClick={() => applyLayout(nodes, edges)}
                  title="Réorganiser automatiquement le graphe"
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-slate-100 text-slate-600 rounded-md hover:bg-slate-200 transition-colors"
                >
                  ⟳ Réorganiser
                </button>
              )}
              {!readOnly && (
                <button
                  onClick={handleSave}
                  disabled={saveStatus === 'saving'}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
                    saveStatus === 'saving'
                      ? 'bg-slate-200 text-slate-500 cursor-wait'
                      : saveStatus === 'saved'
                        ? 'bg-emerald-100 text-emerald-700'
                        : saveStatus === 'error'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {saveStatus === 'saving' && 'Enregistrement…'}
                  {saveStatus === 'saved' && 'Enregistré ✓'}
                  {saveStatus === 'error' && 'Erreur ✗'}
                  {!saveStatus && 'Enregistrer'}
                </button>
              )}
            </div>
          </Panel>

          {/* Guide panel — toggled via the ? button in Controls */}
          {!readOnly && showGuide && (
            <Panel position="bottom-left" style={{ marginBottom: '10rem' }}>
              <div className="bg-white/90 backdrop-blur-sm border border-slate-200 rounded-lg shadow-sm px-4 py-3 text-xs text-slate-500 max-w-xs">
                <ul className="space-y-1 list-disc pl-3">
                  <li>Glissez un état pour le repositionner.</li>
                  <li>Reliez deux états pour créer une transition.</li>
                  <li>Cliquez sur un état ou une transition pour le configurer.</li>
                  <li>
                    Touche <kbd className="font-mono bg-slate-100 px-1 rounded">Delete</kbd> pour
                    supprimer l&apos;élément sélectionné.
                  </li>
                </ul>
              </div>
            </Panel>
          )}
        </ReactFlow>
      </div>

      {/* New state modal */}
      {newStateModalOpen && (
        <NewStateModal
          existingIds={nodes.map((n) => n.id)}
          onConfirm={confirmAddState}
          onCancel={() => setNewStateModalOpen(false)}
        />
      )}

      {/* Right sidebar */}
      {(selectedNode || selectedEdge) && (
        <div className="w-80 bg-white border-l border-slate-200 shadow-xl z-20 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-6">
            {selectedNode && (() => {
              const nodeWeight = selectedNode.data?.weight ?? 0;
              const others = nodes.filter((n) => n.id !== selectedNode.id);
              const isAloneAtWeight = !others.some((n) => (n.data?.weight ?? 0) === nodeWeight);
              const hasLower = others.some((n) => (n.data?.weight ?? 0) < nodeWeight);
              const hasHigher = others.some((n) => (n.data?.weight ?? 0) > nodeWeight);
              const outgoing = edges.filter((e) => e.source === selectedNode.id);
              const incoming = edges.filter((e) => e.target === selectedNode.id);
              return (
                <>
                  <StatePanel
                    node={selectedNode}
                    minWeight={(!hasLower && isAloneAtWeight) ? nodeWeight : undefined}
                    maxWeight={(!hasHigher && isAloneAtWeight) ? nodeWeight : undefined}
                    onChange={updateNode}
                    onDelete={confirmedDeleteElement}
                    readOnly={readOnly}
                  />

                  {/* Transitions section */}
                  <div className="border-t border-slate-100 pt-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                      Transitions
                    </h4>

                    {/* Incoming */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-slate-600">Depuis</span>
                        {!readOnly && (
                          <button
                            onClick={() => setTransitionModal({ direction: 'in', nodeId: selectedNode.id })}
                            className="text-xs text-iris-600 hover:text-iris-800 font-medium"
                          >
                            + Depuis…
                          </button>
                        )}
                      </div>
                      {incoming.length === 0 ? (
                        <p className="text-xs text-slate-400 italic">Aucune transition entrante.</p>
                      ) : (
                        <ul className="flex flex-col gap-1">
                          {incoming.map((e) => {
                            const sourceNode = nodes.find((n) => n.id === e.source);
                            return (
                              <li
                                key={e.id}
                                className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-slate-50 hover:bg-slate-100 group"
                              >
                                <button
                                  className="text-xs text-slate-700 font-medium text-left truncate"
                                  onClick={() => { setSelectedEdge(e); setSelectedNode(null); }}
                                >
                                  ← {sourceNode?.data?.label || e.source}
                                </button>
                                {!readOnly && (
                                  <button
                                    onClick={() => confirmedRemoveTransition(e.id)}
                                    className="text-slate-300 hover:text-red-500 ml-2 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                    title="Supprimer"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>

                    {/* Outgoing */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium text-slate-600">Vers</span>
                        {!readOnly && (
                          <button
                            onClick={() => setTransitionModal({ direction: 'out', nodeId: selectedNode.id })}
                            className="text-xs text-iris-600 hover:text-iris-800 font-medium"
                          >
                            + Vers…
                          </button>
                        )}
                      </div>
                      {outgoing.length === 0 ? (
                        <p className="text-xs text-slate-400 italic">Aucune transition sortante.</p>
                      ) : (
                        <ul className="flex flex-col gap-1">
                          {outgoing.map((e) => {
                            const targetNode = nodes.find((n) => n.id === e.target);
                            return (
                              <li
                                key={e.id}
                                className="flex items-center justify-between px-2.5 py-1.5 rounded-md bg-slate-50 hover:bg-slate-100 group"
                              >
                                <button
                                  className="text-xs text-slate-700 font-medium text-left truncate"
                                  onClick={() => { setSelectedEdge(e); setSelectedNode(null); }}
                                >
                                  → {targetNode?.data?.label || e.target}
                                </button>
                                {!readOnly && (
                                  <button
                                    onClick={() => confirmedRemoveTransition(e.id)}
                                    className="text-slate-300 hover:text-red-500 ml-2 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                    title="Supprimer"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
            {selectedEdge && (
              <TransitionPanel
                edge={selectedEdge}
                roles={roles}
                onChange={updateEdge}
                onDelete={confirmedDeleteElement}
                readOnly={readOnly}
              />
            )}
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {confirmModal && (
        <ConfirmModal
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* Transition picker modal */}
      {transitionModal && (
        <TransitionPickerModal
          direction={transitionModal.direction}
          pivotNodeId={transitionModal.nodeId}
          nodes={nodes}
          existingEdges={edges}
          onConfirm={(selectedIds, label) => {
                        const { direction, nodeId } = transitionModal;
                        const pairs = selectedIds.map((selectedId) =>
                          direction === 'out'
                            ? { source: nodeId, target: selectedId }
                            : { source: selectedId, target: nodeId },
                        );
                        addTransitionsBatch(pairs, label);
                      }}
          onClose={() => setTransitionModal(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component — ReactiveJSON plugin
//
// Accepted props (from YAML):
//   workflowId  string  — Drupal workflow machine name (e.g. 'doc_editorial')
//   readOnly    bool    — optional, disables all editing controls
// ---------------------------------------------------------------------------

/**
 * Generic workflow graph editor, registered as a ReactiveJSON element plugin.
 *
 * Usage in YAML:
 *   - type: WorkflowGraphEditor
 *     workflowId: "doc_editorial"
 *     readOnly: false
 */
export function WorkflowGraphEditor({ props }) {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);

  const workflowId = evaluateTemplateValue({
    valueToEvaluate: props?.workflowId,
    globalDataContext,
    templateContext,
  });

  const readOnly = evaluateTemplateValue({
    valueToEvaluate: props?.readOnly,
    globalDataContext,
    templateContext,
  }) ?? false;

  if (!workflowId) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 text-sm">
        Propriété <code className="mx-1 font-mono bg-slate-100 px-1 rounded">workflowId</code>{' '}
        manquante.
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col font-sans">
      <ReactFlowProvider>
        <WorkflowEditorInner workflowId={workflowId} readOnly={!!readOnly} />
      </ReactFlowProvider>
    </div>
  );
}
